import axios, { InternalAxiosRequestConfig } from "axios";

const APP_SIGNATURE_HEADERS = ["X-App-Key-Id", "X-App-Timestamp", "X-App-Nonce", "X-App-Signature"] as const;

export const APP_SIGNATURE_TIMEOUT_MS = 1500;

export type AppSignature = {
  keyId: string;
  timestamp: string;
  nonce: string;
  signature: string;
};

/**
 * Builds the path the native side signs: pathname + query of the full request URL, without host.
 * Must match what the backend sees as `req.originalUrl`.
 */
export const getSignaturePath = (config: InternalAxiosRequestConfig): string => {
  const url = new URL(axios.getUri(config), window.location.origin);
  return `${url.pathname}${url.search}`;
};

/**
 * Asks the native app to sign the request (`window.nativeDevice.getSignature`) and attaches the result as headers.
 * Sends nothing when the bridge method is missing (browser, old app) or fails; the backend decides what to do.
 */
export const attachAppSignature = async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
  APP_SIGNATURE_HEADERS.forEach((name) => config.headers?.delete?.(name));

  if (typeof window.nativeDevice?.getSignature !== "function") return config;

  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), APP_SIGNATURE_TIMEOUT_MS);
    });
    const result = await Promise.race([
      window.nativeDevice.getSignature({
        method: (config.method ?? "get").toUpperCase(),
        path: getSignaturePath(config),
      }),
      timeout,
    ]);
    if (result?.keyId && result.timestamp && result.nonce && result.signature) {
      config.headers["X-App-Key-Id"] = result.keyId;
      config.headers["X-App-Timestamp"] = result.timestamp;
      config.headers["X-App-Nonce"] = result.nonce;
      config.headers["X-App-Signature"] = result.signature;
    }
  } catch {
    // Signing is best-effort on the client; never block the request
  } finally {
    clearTimeout(timer);
  }
  return config;
};
